import { IOperationResult } from "@Totum/QuestionnaireBuilder/Application/types";
import {
    createOperationResult,
    processTreeItemAddition,
    processTreeItemRemoval,
} from "@Totum/QuestionnaireBuilder/Application/utils";
import useQuestionnaireDomain from "@Totum/QuestionnaireBuilder/Domain/hooks/useQuestionnaireDomain";
import { useCurrentQuestionnaireDetails } from "@Totum/QuestionnaireBuilder/State/hooks/useCurrentQuestionnaireDetails";
import {
    useCreateQuestionMutation,
    useCreateQuestionnaireMutation,
    useDeleteQuestionMutation,
    useUpdateQuestionMutation,
    useUpdateQuestionnaireMutation,
} from "@Totum/QuestionnaireBuilder/State/mutations";
import { useActivityCategoriesTree } from "@Totum/Main/Components/shared/hooks/useActivityCategoriesTree";
import { useDefinitionsActivitiesAndCategoriesQuery } from "@Totum/Main/Components/shared/queries";
import { isPresent, ITreeItem } from "@Totum/Main/Utils";

/**
 * Orchestrates the operations for multiple Form components.
 * This interacts with the questionnaire domain and the server side state (react query).
 *
 * @param currentQuestionnaireId The ID of the current questionnaire
 */
const useFormOperations = () => {
    const [currentQuestionnaireDetails, setCurrentQuestionnaireDetails] = useCurrentQuestionnaireDetails();
    // ** Hooks ** //
    const createQuestionnaireMutation = useCreateQuestionnaireMutation();
    const updateQuestionnaireMutation = useUpdateQuestionnaireMutation();
    const createQuestionMutation = useCreateQuestionMutation();
    const deleteQuestionMutation = useDeleteQuestionMutation();
    const updateQuestionMutation = useUpdateQuestionMutation();
    const { categoryIds, parentCategoryIds } = useDefinitionsActivitiesAndCategoriesQuery();
    const activityCategoriesTree = useActivityCategoriesTree();

    // ** Constants & Variables ** //
    const questionnaireDomain = useQuestionnaireDomain(currentQuestionnaireDetails);

    // ** Functions ** //

    /**
     * Delete the questionnaire
     */
    function deleteQuestionnaire(questionnaireId: string): void {
        // throw new Error("Function not implemented.");
    }

    /**
     * Update the title or description of the questionnaire
     * @param title The new title
     * @param description The new description
     */
    const updateTitleOrDescription = async (title: string, description: string): Promise<IOperationResult> => {
        questionnaireDomain.updateTitleAndDescription(title, description);

        return await saveQuestionnaire();
    };

    /**
     * Update the number of CME points associated with the questionnaire
     * @param points The number of points
     */
    const updatePoints = async (points: number): Promise<IOperationResult> => {
        questionnaireDomain.updatePointsAssociation(points);

        return await saveQuestionnaire();
    };

    /**
     * Add an activity or category to the questionnaire
     * @param value the id of the activity or category
     * @param item a tree item
     */
    const addTreeItem = async (valueWithPrefix: string, item: ITreeItem): Promise<IOperationResult> => {
        const updatedAssociations = processTreeItemAddition(
            valueWithPrefix,
            item,
            activityCategoriesTree as ITreeItem[], // This type is the same
            valueWithPrefix.includes("cat-")
                ? questionnaireDomain.details.associationCollection.activities
                : questionnaireDomain.details.associationCollection.activityCategories
        );
        questionnaireDomain.updateAssociations(updatedAssociations);

        return await saveQuestionnaire();
    };

    /**
     * Remove an activity or category from the questionnaire
     * @param value the id of the activity or category
     * @param item a tree item
     */
    const removeTreeItem = async (valueWithPrefix: string, item: ITreeItem): Promise<IOperationResult> => {
        const updatedAssociations = processTreeItemRemoval(
            valueWithPrefix,
            item,
            activityCategoriesTree as ITreeItem[], // This type is the same
            valueWithPrefix.includes("cat-")
                ? questionnaireDomain.details.associationCollection.activities
                : questionnaireDomain.details.associationCollection.activityCategories
        );

        questionnaireDomain.updateAssociations(updatedAssociations);

        return await saveQuestionnaire();
    };

    const updateLearningObjectives = async (description: string): Promise<IOperationResult> => {
        questionnaireDomain.updateLearningObjectives(description);

        return await saveQuestionnaire();
    };

    const addQuestion = (): void => {
        questionnaireDomain.addQuestion();

        setCurrentQuestionnaireDetails(questionnaireDomain.details);
    };

    const deleteQuestion = async (questionId: string): Promise<IOperationResult> => {
        questionnaireDomain.removeQuestion(questionId);

        const deletionResult = await deleteQuestionMutation.mutateAsync(questionId);
        return createOperationResult(deletionResult.isSuccess);
    };

    const updateChoices = async (questionId: string, values: string[]): Promise<IOperationResult> => {
        questionnaireDomain.updateChoices(questionId, values);

        return await saveQuestion(questionId);
    };

    const updateCorrectChoice = async (questionId: string, choiceText: string): Promise<IOperationResult> => {
        questionnaireDomain.updateCorrectChoice(questionId, choiceText);

        return await saveQuestion(questionId);
    };

    const updateQuestionIsScored = async (questionId: string, isScored: boolean): Promise<IOperationResult> => {
        questionnaireDomain.updateQuestionIsScored(questionId, isScored);

        return await saveQuestion(questionId);
    };

    const updateQuestionTitle = async (questionId: string, title: string): Promise<IOperationResult> => {
        questionnaireDomain.updateQuestionTitle(questionId, title);

        return await saveQuestion(questionId);
    };

    const updateQuestionType = async (questionId: string, type: string): Promise<IOperationResult> => {
        questionnaireDomain.updateQuestionType(questionId, type);

        return await saveQuestion(questionId);
    };

    /**
     * Create or update the questionnaire
     */
    const saveQuestionnaire = async () => {
        if (!questionnaireDomain.validate()) {
            return createOperationResult();
        }

        const model = questionnaireDomain.toQuestionnaireDetails();

        const mutation = isPresent(model.questionnaireId) ? updateQuestionnaireMutation : createQuestionnaireMutation;

        const mutationResult = await mutation.mutateAsync(model);

        return createOperationResult(mutationResult.isSuccess);
    };

    /**
     * Create or update the question
     */
    const saveQuestion = async (questionId: string) => {
        const model = questionnaireDomain.toQuestionnaireDetails().questions.find((q) => q.questionId === questionId);

        if (!model || !questionnaireDomain.validateQuestion(model.questionId)) {
            return createOperationResult();
        }

        // Don't progress if we are already creating
        if (createQuestionMutation.isLoading) {
            return createOperationResult();
        }

        const mutation = isPresent(model.questionId) ? updateQuestionMutation : createQuestionMutation;
        const mutationResult = await mutation.mutateAsync(model);

        return createOperationResult(mutationResult.isSuccess);
    };

    // ** Exports ** //

    return {
        // data
        questionnaire: {
            questionnaireId: questionnaireDomain.details.questionnaireId,

            associationCollection: questionnaireDomain.details.associationCollection,
            description: questionnaireDomain.details.description,
            title: questionnaireDomain.details.title,
            points: questionnaireDomain.details.points,
            isPublished: questionnaireDomain.details.isPublished,
            isLocked: questionnaireDomain.details.isLocked,
            questions: questionnaireDomain.details.questions,
            learningObjectiveDetails: questionnaireDomain.details.learningObjectiveDetails,

            // questionnaire flags
            isExistingQuestionnaire: Boolean(questionnaireDomain.isExistingQuestionnaire),
            hasQuestions: Boolean(questionnaireDomain.hasQuestions),
        },
        activityCategoriesTree,
        categorySets: {
            categoryIds,
            parentCategoryIds,
        },

        // methods
        addTreeItem,
        removeTreeItem,

        updateTitleOrDescription,
        updatePoints,

        updateLearningObjectives,

        addQuestion,
        deleteQuestion,
        updateChoices,
        updateCorrectChoice,
        updateQuestionIsScored,
        updateQuestionTitle,
        updateQuestionType,

        // state
        isDeleting: deleteQuestionMutation.isLoading,
    };
};

export default useFormOperations;
